import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import secureStorage from "../utils/secureStorage";
import { useNavigate } from "react-router-dom";
import { Baby, Palette, Utensils, BookOpen, Settings, ArrowLeft, Candy, IceCreamCone } from "lucide-react";
import { toast } from "sonner";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import { PageLayout } from "../components/layout/Sidebar";
import PullToRefresh from "../components/common/PullToRefresh";
import PushNotificationPrompt from "../components/notifications/PushNotificationPrompt";
import AdminNotifications from "../components/notifications/AdminNotifications";
import { FloatingElements, AnimatedStatCard, AnimatedHeader, QuickActionButton, GreetingBanner, AnimatedCard, TeacherIcon } from "../components/ui/AnimatedElements";
import { BirthdayNotificationCard, useBirthdayChildren } from "../components/notifications/BirthdayNotification";
import { useModuleAccess } from "../hooks/useModuleAccess";
import TemaCountdownWidget from "../components/widgets/TemaCountdownWidget";
import PendingAttendanceWidget from "../components/widgets/PendingAttendanceWidget";
import BirthdayReminderWidget from "../components/widgets/BirthdayReminderWidget";

export default function GuruDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { birthdayChildren } = useBirthdayChildren(user);
  const { isModuleOn } = useModuleAccess(user?.role);
  const [children, setChildren] = useState([]);
  const [todayActivities, setTodayActivities] = useState(0);
  const [nutritionalSummary, setNutritionalSummary] = useState({
    total_children: 0,
    normal_children: 0,
    malnourished_children: 0,
    no_data_or_out_of_range: 0
  });
  
  // Check if this is an impersonated session
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalManagerName, setOriginalManagerName] = useState("");
  
  useEffect(() => {
    const originalToken = secureStorage.getItem("original_manager_token");
    const originalUser = secureStorage.getItem("original_manager_user");
    const superadminToken = secureStorage.getItem("original_superadmin_token");
    const superadminUser = secureStorage.getItem("original_superadmin_user");
    if (originalToken && originalUser) {
      setIsImpersonating(true);
      try {
        const userData = JSON.parse(originalUser);
        setOriginalManagerName(userData.name || "Pengurus Cawangan");
      } catch (e) {
        setOriginalManagerName("Pengurus Cawangan");
      }
    } else if (superadminToken && superadminUser) {
      // Superadmin impersonate guru terus dari tab Pengguna
      setIsImpersonating(true);
      try {
        const userData = JSON.parse(superadminUser);
        setOriginalManagerName(userData.name || "Super Admin");
      } catch (e) {
        setOriginalManagerName("Super Admin");
      }
    } else {
      setIsImpersonating(false);
      setOriginalManagerName("");
    }
  }, []);
  
  // Handle return to original dashboard
  const handleReturnToManager = () => {
    const originalToken = secureStorage.getItem("original_manager_token");
    const originalUser = secureStorage.getItem("original_manager_user");
    const superadminToken = secureStorage.getItem("original_superadmin_token");
    const superadminUser = secureStorage.getItem("original_superadmin_user");
    
    if (originalToken && originalUser) {
      // Restore original token and user
      secureStorage.setItem("token", originalToken);
      secureStorage.setItem("user", originalUser);
      
      // Clear impersonation data
      secureStorage.removeItem("original_manager_token");
      secureStorage.removeItem("original_manager_user");
      
      toast.success("Kembali ke Dashboard asal");
      
      // Reload to apply changes
      window.location.href = "/";
    } else if (superadminToken && superadminUser) {
      // Restore superadmin token and user
      secureStorage.setItem("token", superadminToken);
      secureStorage.setItem("user", superadminUser);
      
      secureStorage.removeItem("original_superadmin_token");
      secureStorage.removeItem("original_superadmin_user");
      
      toast.success("Kembali ke Dashboard Super Admin");
      
      window.location.href = "/";
    }
  };

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/children');
      const filteredChildren = response.data.filter(c => 
        c.class_name && !c.class_name.toLowerCase().includes('pre-school') && !c.class_name.toLowerCase().includes('preschool')
      );
      setChildren(filteredChildren);
    } catch (error) {
    }
  }, []);

  const fetchTodayActivities = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/stats');
      setTodayActivities(response.data.today_activities || 0);
    } catch (error) {
    }
  }, []);

  const fetchNutritionalSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/nutrition-status/summary');
      setNutritionalSummary(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [childrenRes, statsRes, nutritionRes] = await Promise.allSettled([
          axiosInstance.get('/children'),
          axiosInstance.get('/stats'),
          axiosInstance.get('/nutrition-status/summary')
        ]);
        
        if (childrenRes.status === 'fulfilled') {
          const filteredChildren = childrenRes.value.data.filter(c => 
            c.class_name && !c.class_name.toLowerCase().includes('pre-school') && !c.class_name.toLowerCase().includes('preschool')
          );
          setChildren(filteredChildren);
        }
        if (statsRes.status === 'fulfilled') setTodayActivities(statsRes.value.data.today_activities || 0);
        if (nutritionRes.status === 'fulfilled') setNutritionalSummary(nutritionRes.value.data);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
    };
    loadDashboardData();
  }, []);

  const handlePullRefresh = async () => {
    await Promise.all([fetchChildren(), fetchTodayActivities(), fetchNutritionalSummary()]);
    toast.success("Data dikemaskini! 🎉");
  };

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      
      <PullToRefresh onRefresh={handlePullRefresh}>
      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Greeting Banner with Notification */}
        <div className="relative">
          <GreetingBanner userName={user.name} role={user.role} userPhoto={user.photo_url} />
          {/* Notification Bell - positioned at top-right of banner */}
          <div className="absolute top-4 right-4 z-10">
            <AdminNotifications user={user} />
          </div>
        </div>
        
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl shadow-lg border-2 border-amber-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <span className="text-xl">👤</span>
                </div>
                <div>
                  <p className="text-white font-bold text-sm">
                    Anda sedang melihat dashboard sebagai <span className="font-black">{user.name}</span>
                  </p>
                  <p className="text-amber-100 text-xs font-medium">
                    Sesi asal: {originalManagerName}
                  </p>
                </div>
              </div>
              <button
                onClick={handleReturnToManager}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-bold text-sm rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Kembali
              </button>
            </div>
          </div>
        )}
        
        {/* Birthday Notification Card for Teachers */}
        {birthdayChildren.length > 0 && isModuleOn('birthday_notification') && (
          <BirthdayNotificationCard children={birthdayChildren} userRole={user.role} />
        )}
        
        {/* Tema Countdown Widget */}
        <TemaCountdownWidget user={user} />
        
        {/* Pending Attendance Widget - before 3 PM */}
        <PendingAttendanceWidget user={user} />
        
        {/* Birthday Reminder Widget - tomorrow's birthdays */}
        <BirthdayReminderWidget user={user} />
        
        <div className="flex justify-between items-center mb-4">
          <AnimatedHeader 
            customIcon={
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 sm:w-7 sm:h-7 text-white">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                  <path d="M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
              </div>
            }
            gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            title="Dashboard Guru"
            subtitle="🎪 Urus kelas anda dengan mudah!"
            delay={100}
          />
          {/* Notification moved to GreetingBanner area */}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 lg:gap-6 mb-8">
          <AnimatedStatCard icon={Baby} label="Kanak-kanak Saya" value={children.length} color="orange" delay={200} />
          <AnimatedStatCard icon={Palette} label="Aktiviti Hari Ini" value={todayActivities} color="blue" delay={300} />
        </div>

        {/* Nutritional Status */}
        <AnimatedCard className="mb-8" delay={400} color="green">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-400 to-cyan-400 flex items-center justify-center shadow-lg">
              <span className="text-2xl">⚖️</span>
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-800">Status BMI</h2>
              <p className="text-sm font-bold text-gray-500">Tidak termasuk Pre-school</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Jumlah */}
            <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.total_children}</p>
                <p className="text-sm font-bold text-white/90">Jumlah</p>
              </div>
            </div>
            
            {/* Normal */}
            <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.normal_children}</p>
                <p className="text-sm font-bold text-white/90">Normal</p>
              </div>
            </div>
            
            {/* Perlu Perhatian */}
            <div className="bg-gradient-to-br from-rose-400 to-rose-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.malnourished_children}</p>
                <p className="text-sm font-bold text-white/90">Perlu Perhatian</p>
              </div>
            </div>
            
            {/* Tiada Data */}
            <div className="bg-gradient-to-br from-slate-400 to-slate-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.no_data_or_out_of_range}</p>
                <p className="text-sm font-bold text-white/90">Tiada Data</p>
              </div>
            </div>
          </div>
        </AnimatedCard>

        {/* Quick Actions */}
        <AnimatedCard className="mb-8" delay={500} color="purple">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center shadow-lg">
              <span className="text-2xl">⚡</span>
            </div>
            <h2 className="text-2xl font-black text-gray-800">Akses Pantas</h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionButton emoji="👶" label="Kanak-kanak" sublabel="Lihat senarai" color="red" onClick={() => navigate('/children')} delay={600} />
            <QuickActionButton emoji="🎨" label="Aktiviti" sublabel="Rekod aktiviti" color="orange" onClick={() => navigate('/activities')} delay={700} />
            <QuickActionButton emoji="🍽️" label="Menu" sublabel="Log pemakanan" color="green" onClick={() => navigate('/nutrition')} delay={800} />
            <QuickActionButton emoji="✅" label="Kehadiran" sublabel="Rekod kehadiran" color="teal" onClick={() => navigate('/attendance')} delay={850} />
            <QuickActionButton emoji="⚙️" label="Tetapan" sublabel="Kemaskini profil" color="blue" onClick={() => navigate('/tetapan-guru')} delay={900} />
          </div>
        </AnimatedCard>

        {/* Guide Card */}
        <AnimatedCard className="mb-8" delay={700} color="cyan">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-xl">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-800">Panduan Penggunaan</h2>
                  <p className="text-sm font-bold text-cyan-500">Destiny Junior v2.0</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4 font-bold">
                Panduan lengkap untuk guru & pendidik. Termasuk cara rekod aktiviti, log harian, pemakanan, dan banyak lagi.
              </p>
              
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { title: "Log Masuk", icon: "🔐" },
                  { title: "Aktiviti", icon: "🎨" },
                  { title: "Log Harian", icon: "📋" },
                  { title: "Menu", icon: "🍝" },
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center text-xs font-bold text-gray-600 bg-gray-100 rounded-xl px-2 py-3 hover:bg-cyan-100 transition-colors">
                    <span className="text-2xl mb-1">{item.icon}</span>
                    <span>{item.title}</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => navigate('/panduan?tab=guru')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-black rounded-2xl hover:from-cyan-500 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                <BookOpen className="w-5 h-5" />
                Buka Panduan 📖
              </button>
            </div>
            
            <div className="hidden md:block ml-6">
              <img 
                src="/logo-destiny.png" 
                alt="Destiny Junior" 
                className="w-28 h-28 object-contain"
                style={{ animation: 'bounceFloat 3s ease-in-out infinite' }}
              />
            </div>
          </div>
        </AnimatedCard>
        
      </div>
      </PullToRefresh>
      
      <style>{`
        @keyframes bounceFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }
      `}</style>
    </PageLayout>
  );
}
